import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import {
  CheckCircle as AcceptIcon,
  Cancel as RejectIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import FormField from "./FormField";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { useMutation } from "@apollo/client";
import { INVITATION_REGISTER } from "../graphql/queries";
import countries from "../countries.json";
import {
  extractReferralParams,
  initializeReferralTracking,
  prepareReferralFormData,
} from "../utils/referralUtils";

const InvitationRegistrationForm = ({
  invitationData,
  fields,
  eventName,
  capacity,
  uid,
  onSuccess,
  onError,
}) => {
  console.log("🔍 InvitationRegistrationForm received uid:", uid);

  // Extract referral parameters
  const referralParams = extractReferralParams();

  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAction, setSubmitAction] = useState(null);
  const [error, setError] = useState(null);

  // State for country and mobile code management
  const [selectedCountry, setSelectedCountry] = useState("Oman");
  const [selectedMobileCode, setSelectedMobileCode] = useState("+968");

  // Function to find country code by dial code
  const findCountryCodeByDialCode = (dialCode) => {
    const country = countries.find((c) => c.dial_code === dialCode);
    return country ? country.label : "Oman";
  };

  // Build validation schema from fields
  const buildValidationSchema = () => {
    const schema = {};
    fields.forEach((field) => {
      if (field.required) {
        let fieldSchema = yup.string();

        if (field.type === "email") {
          fieldSchema = fieldSchema.email("Please enter a valid email address");
        }

        if (field.minLength) {
          fieldSchema = fieldSchema.min(
            field.minLength,
            `Minimum ${field.minLength} characters required`
          );
        }

        if (field.maxLength) {
          fieldSchema = fieldSchema.max(
            field.maxLength,
            `Maximum ${field.maxLength} characters allowed`
          );
        }

        if (field.pattern) {
          const regex = new RegExp(field.pattern);
          fieldSchema = fieldSchema.matches(regex, "Invalid format");
        }

        schema[field.name] = fieldSchema.required(
          `${field.label || field.name} is required`
        );
      }
    });
    return yup.object().shape(schema);
  };

  const validationSchema = buildValidationSchema();
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm({
    resolver: async (data) => {
      try {
        const validatedData = await validationSchema.validate(data, {
          abortEarly: false,
        });
        return { values: validatedData, errors: {} };
      } catch (err) {
        const errors = {};
        err.inner.forEach((error) => {
          errors[error.path] = { message: error.message };
        });
        return { values: {}, errors };
      }
    },
    defaultValues: fields.reduce((acc, field) => {
      // Use invitation data if available, otherwise use field default value
      acc[field.name] =
        invitationData?.[field.name] || field.defaultValue || "";
      return acc;
    }, {}),
  });

  const [invitationRegister] = useMutation(INVITATION_REGISTER);

  // Set default values for country and calling_code fields
  useEffect(() => {
    const countryField = fields.find((f) => f.type === "country");
    const callingCodeField = fields.find(
      (f) => f.type === "calling_code" || f.type === "mobile_code"
    );

    if (countryField && !countryField.defaultValue) {
      setValue(countryField.name, "Oman");
    }

    if (callingCodeField && !callingCodeField.defaultValue) {
      setValue(callingCodeField.name, "+968");
    }
  }, [fields, setValue]);

  const handleAccept = async (data) => {
    setIsSubmitting(true);
    setSubmitAction("accept");
    setError(null);

    try {
      // Add referral data if present
      const finalFormData = prepareReferralFormData(data, referralParams);

      const result = await invitationRegister({
        variables: {
          uid: uid,
          userData: JSON.stringify(finalFormData),
          action: "accept",
        },
      });

      if (result.data?.invitationRegister?.success) {
        onSuccess?.(result.data.invitationRegister);
      } else {
        throw new Error(
          result.data?.invitationRegister?.message || "Registration failed"
        );
      }
    } catch (err) {
      console.error("Accept registration error:", err);
      setError(err.message);
      onError?.(err);
    } finally {
      setIsSubmitting(false);
      setSubmitAction(null);
    }
  };

  const handleReject = async () => {
    setIsSubmitting(true);
    setSubmitAction("reject");
    setError(null);

    try {
      const currentData = watch();
      const result = await invitationRegister({
        variables: {
          uid: uid,
          userData: JSON.stringify(currentData),
          action: "reject",
        },
      });

      if (result.data?.invitationRegister?.success) {
        // Handle custom response for rejection
        if (result.data.invitationRegister.customResponse) {
          // Create the custom rejection response and pass it to onSuccess
          const customRejectionResponse = `<div style="text-align: center; padding: 60px 40px; font-family: Arial, sans-serif; background-color: #f8f9fa; min-height: 100vh; display: flex; align-items: center; justify-content: center;">
            <div style="background-color: white; border-radius: 16px; padding: 48px; box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08); border: 1px solid #e9ecef; max-width: 600px; width: 100%; position: relative; overflow: hidden;">
              <div style="position: absolute; top: 0; left: 0; right: 0; height: 4px; background: linear-gradient(90deg, #6c757d, #adb5bd);"></div>
              
              <div style="color: #6c757d; margin-bottom: 24px;">
                <svg width="80" height="80" viewBox="0 0 24 24" fill="currentColor" style="display: block; margin: 0 auto;">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                </svg>
              </div>
              
              <h1 style="color: #495057; margin-bottom: 24px; font-weight: 600; font-size: 2.2rem;">Invitation Status</h1>
              
              <div style="background-color: #f8f9fa; border: 2px solid #dee2e6; border-radius: 12px; padding: 32px; margin-bottom: 24px; border-left: 6px solid #6c757d;">
                <p style="color: #495057; font-weight: 500; line-height: 1.4; font-size: 1.3rem; margin: 0;">${result.data.invitationRegister.message}</p>
              </div>
              
              <p style="color: #6c757d; font-weight: 400; font-size: 1.1rem; margin-bottom: 32px;">Thank you for your response.</p>
              
              <div style="display: flex; justify-content: center; gap: 8px;">
                <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #dee2e6;"></div>
                <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #6c757d;"></div>
                <div style="width: 8px; height: 8px; border-radius: 50%; background-color: #dee2e6;"></div>
              </div>
            </div>
          </div>`;

          // Pass the custom rejection response to parent via onSuccess with special flag
          onSuccess?.({
            ...result.data.invitationRegister,
            customRejectionResponse: customRejectionResponse,
            isRejection: true,
          });
        } else {
          // Only call onSuccess for actual registrations (acceptances)
          onSuccess?.(result.data.invitationRegister);
        }
      } else {
        throw new Error(
          result.data?.invitationRegister?.message || "Rejection failed"
        );
      }
    } catch (err) {
      console.error("Reject invitation error:", err);
      setError(err.message);
      onError?.(err);
    } finally {
      setIsSubmitting(false);
      setSubmitAction(null);
    }
  };

  const getFieldIcon = (fieldName) => {
    const name = fieldName.toLowerCase();
    if (name.includes("name")) return <PersonIcon />;
    if (name.includes("email")) return <EmailIcon />;
    if (name.includes("company")) return <BusinessIcon />;
    if (name.includes("mobile") || name.includes("phone")) return <PhoneIcon />;
    if (name.includes("country") || name.includes("address"))
      return <LocationIcon />;
    return <PersonIcon />;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto", p: 1 }}>
        {/* Invitation Header */}
        <Card
          sx={{
            mb: 3,
            background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.main} 100%)`,
            color: "white",
          }}
        >
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <AcceptIcon sx={{ fontSize: 60, mb: 2 }} />
            </motion.div>
            <Typography
              variant="h4"
              color={theme.palette.primary.contrastText}
              component="h1"
              gutterBottom
            >
              You're Invited!
            </Typography>
            <Typography
              variant="h6"
              color={theme.palette.primary.contrastText}
              sx={{ mb: 2 }}
            >
              {eventName}
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              justifyContent="center"
              flexWrap="wrap"
            >
              <Chip
                label={`Role: ${invitationData.role}`}
                variant="outlined"
                sx={{ color: "white", borderColor: "white" }}
              />
              <Chip
                label={`Company: ${invitationData.userCompany}`}
                variant="outlined"
                sx={{ color: "white", borderColor: "white" }}
              />
            </Stack>
          </CardContent>
        </Card>

        {/* Capacity Information */}
        {capacity && (
          <Alert
            severity={capacity.available ? "success" : "warning"}
            sx={{ mb: 3 }}
            icon={capacity.available ? <AcceptIcon /> : <RejectIcon />}
          >
            <Typography variant="body1">
              <strong>Registration Status:</strong> {capacity.message}
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {capacity.current} of {capacity.allowed} seats used (
              {capacity.remaining} remaining)
            </Typography>
          </Alert>
        )}

        {/* Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Registration Form */}
        <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 3 }}>
            Complete Your Registration
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            Please review and update your information below. Fields marked with
            an asterisk (*) are required.
          </Typography>

          <form onSubmit={handleSubmit(handleAccept)}>
            <Grid container spacing={2}>
              {fields.map((field, index) => (
                <Grid
                  item
                  xs={field.xs || 12}
                  sm={field.sm || field.xs || 12}
                  md={field.md || field.xs || 12}
                  lg={field.md || field.xs || 12}
                  key={field.name}
                >
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Controller
                      name={field.name}
                      control={control}
                      render={(rhf) => (
                        <FormField
                          rhf={rhf}
                          f={field}
                          disabled={isSubmitting}
                          featuresList={[]}
                          setValue={setValue}
                          findCountryCodeByDialCode={findCountryCodeByDialCode}
                          selectedCountry={selectedCountry}
                          setSelectedCountry={setSelectedCountry}
                          selectedMobileCode={selectedMobileCode}
                          setSelectedMobileCode={setSelectedMobileCode}
                          uid={uid}
                        />
                      )}
                    />
                  </motion.div>
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={
                    isSubmitting && submitAction === "accept" ? (
                      <CircularProgress size={20} />
                    ) : (
                      <AcceptIcon />
                    )
                  }
                  disabled={isSubmitting}
                  sx={{
                    minWidth: 160,
                    py: 1.5,
                    background: `linear-gradient(45deg, ${theme.palette.success.main} 30%, ${theme.palette.success.dark} 90%)`,
                    "&:hover": {
                      background: `linear-gradient(45deg, ${theme.palette.success.dark} 30%, ${theme.palette.success.main} 90%)`,
                    },
                  }}
                >
                  {isSubmitting && submitAction === "accept"
                    ? "Processing..."
                    : "I Accept"}
                </Button>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  size="large"
                  startIcon={
                    isSubmitting && submitAction === "reject" ? (
                      <CircularProgress size={20} />
                    ) : (
                      <RejectIcon />
                    )
                  }
                  disabled={isSubmitting}
                  onClick={handleReject}
                  sx={{
                    minWidth: 160,
                    py: 1.5,
                    borderColor: theme.palette.error.main,
                    color: theme.palette.error.main,
                    "&:hover": {
                      borderColor: theme.palette.error.dark,
                      backgroundColor: alpha(theme.palette.error.main, 0.04),
                    },
                  }}
                >
                  {isSubmitting && submitAction === "reject"
                    ? "Processing..."
                    : "I Reject"}
                </Button>
              </motion.div>
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 3, textAlign: "center" }}
            >
              By clicking "I Accept", you confirm your registration for this
              event.
              <br />
              By clicking "I Reject", you decline this invitation.
            </Typography>
          </form>
        </Paper>
      </Box>
    </motion.div>
  );
};

export default InvitationRegistrationForm;
